import { GridApi } from 'ag-grid-community';
import { memo, useEffect, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { CHECK, TABLE_HEIGHT, Tabs } from 'common/constants';
import { ComposedTable } from 'components/ComposedTable/ComposedTable';
import { useFlagValue } from 'hooks';
import { PaymentMethod } from 'pages/HighVolumeLandingPage/constants';
import {
  getSelectedTab,
  getSelectedTabToBePaid
} from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getUserRoleIsApOfficeManager } from 'pages/LandingPage/slice';
import {
  getPaymentMethods,
  getSelectedInvoices,
  getSelectedTotal
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { VendorsTableDataType } from 'pages/VendorsTablePage/components/VendorsTable/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { processLandingPageData } from '../helpers';
import { gridColumnLabelMessages, messages } from '../messages';
import {
  clearUpdatedInvoiceDescriptions,
  setAgGridParamsApi,
  updateVendorInvoiceRecords
} from '../slice';
import { JPMCFieldsType } from '../types';
import { useInvoiceColumns } from './useInvoiceTableColumns';

const InvoiceTable = ({
  selectedCIBAccount,
  paymentDate,
  selectedCashCompany,
  selectedCashInBank,
  selectedJournal,
  postingDescription
}: JPMCFieldsType): React.JSX.Element => {
  const intl: IntlShape = useIntl();
  const dispatch = useAppDispatch();
  const selectedTabToBePaid = useAppSelector(getSelectedTabToBePaid);
  const selectedInvoices: VendorsTableDataType[] =
    useAppSelector(getSelectedInvoices);

  const paymentMethods = useAppSelector(getPaymentMethods);
  const selectedTab = useAppSelector(getSelectedTab);
  const selectedTotal = useAppSelector(getSelectedTotal);

  const userRoleIsApOfficeManager = useAppSelector(
    getUserRoleIsApOfficeManager
  );
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');

  useEffect(() => {
    dispatch(
      updateVendorInvoiceRecords(processLandingPageData(selectedInvoices))
    );
  }, [dispatch, selectedInvoices]);

  useEffect(() => {
    dispatch(clearUpdatedInvoiceDescriptions());
  }, [dispatch]);

  const tableHeaderMessage = useMemo(() => {
    if (selectedTab === Tabs.ToBePaid) {
      return messages.invoices;
    }
    if (
      paymentMethods?.includes(landingPageFlag ? PaymentMethod.AP_CHECK : CHECK)
    ) {
      return messages.payByCheck;
    }
    return messages.payByCreditCard;
  }, [landingPageFlag, paymentMethods, selectedTab]);

  const getGridApiParams = (gridApi: GridApi<VendorsTableDataType>) => {
    dispatch(setAgGridParamsApi(gridApi));
  };

  const columns = useInvoiceColumns();

  const tableHeaderFilterData = () => {
    if (interCompanyFlag) {
      return {
        total: selectedTotal,
        selectedPaymentMethods: paymentMethods.join(','),
        cashCompany: selectedCashCompany?.label,
        cashAccount: selectedCashInBank?.label,
        cashDisbursementsJournal: selectedJournal?.label,
        postingDescription,
        paymentDate
      };
    }
    return {
      total: selectedTotal,
      paymentDate: paymentDate as string,
      selectedCIBAccount: selectedCIBAccount as string,
      postingDescription: postingDescription as string,
      selectedPaymentMethods: paymentMethods.join(',')
    };
  };
  return (
    <ComposedTable<VendorsTableDataType>
      columnDefs={columns}
      rowData={selectedInvoices}
      tableHeaderTitle={intl.formatMessage(tableHeaderMessage)}
      isDownloadable={selectedTabToBePaid || userRoleIsApOfficeManager}
      downloadDialogTitle={intl.formatMessage(
        selectedTabToBePaid
          ? gridColumnLabelMessages.invoiceExportDialogTitle
          : gridColumnLabelMessages.invoiceExportAuthorizeDialogTitle
      )}
      isPrintable={userRoleIsApOfficeManager}
      tableHeaderFilters={tableHeaderFilterData()}
      isSearchable={false}
      rowHeight={40}
      customHeight={TABLE_HEIGHT}
      scrollToTopButtonShow
      getGridApiParams={getGridApiParams}
      getRowId={param => param.data.id}
    />
  );
};

export default memo(InvoiceTable);
